import { createSlice } from "@reduxjs/toolkit";
import { AssistentService } from "connection/ai-assistent";
import { exit } from "./authorization";


const initialState = {
    isLoading: false,
    titleLoadingId: null,
    titleLoadingError: null,
    sessions: [],
    session: null,
    messages: [],
    error: null,
    errorTimestamp: null,
    errorNotified: false,
    requestTimestamp: null,  // время отправки запроса
    statistic: {},
    health: null,
    models: []
};

const handleError = (e, dispatch, type) => {

    const code = e.response?.status;
    const message =
        e.response?.data?.ApiError?.message ||
        e.response?.data?.message ||
        e.message ||
        "AI error";

    if (code === 401) {
        dispatch(exit('auth'));
    } else if (code >= 400 || code >= 500) {
        console.error("Server error:", e);
    //} else if (code >= 400) {
        if(type === 'title'){
            dispatch(setTitleLoadingError(message))
        }
        else
            dispatch(setError(message));
    }

    if (code >= 500) {
        dispatch(checkAndNotifySlowError());
    }
};

const aiAssistantSlice = createSlice({
    name: "aiAssistant",
    initialState,
    reducers: {
        setLoading(state, action) {
            state.isLoading = action.payload;
        },

        setTitleLoading(state, action) {
            state.titleLoadingId = action.payload; // null или id сессии
        },

        setTitleLoadingError(state, action) {
            state.titleLoadingError = action.payload; // null или id сессии
        },

        setSessions(state, action) {
            state.sessions = action.payload;
        },

        setSession(state, action) {
            state.session = action.payload;
            state.messages = action.payload?.messages || [];
        },

        addMessage(state, action) {
            state.messages.push(action.payload);
        },

        setRequestTimestamp(state) {
            state.requestTimestamp = new Date().toISOString();
        },

        setError(state, action) {
            state.error = action.payload;
        },

        setErrorNotified(state, action) {
            state.errorNotified = action.payload;
        },

        setStatistic(state, action) {
            state.statistic = action.payload;
        },

        setHealth(state, action) {
            state.health = action.payload;
        },

        setModels(state, action) {
            state.models = action.payload.models; // берём массив из { models: [...] }
        },

        clearChat(state) {
            state.messages = [];
        },

        clearError(state) {
            state.error = null;
        },

        appendToLastMessage: (state, action) => {
            const last = state.messages[state.messages.length - 1];
            if (last && last.role === "assistant") {
                last.content += action.payload;
            }
        },
    }
});

export const {
    setLoading,
    setTitleLoading,
    setTitleLoadingError,
    setSessions,
    setSession,
    addMessage,
    setError,
    clearChat,
    setStatistic,
    setHealth,
    setModels,
    setErrorNotified,
    setRequestTimestamp
} = aiAssistantSlice.actions;



/*export const sendMessageToAi = (payload) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        dispatch(setError(null));
        dispatch(addMessage({
            role: "user",
            content: payload.message,
            created_at: new Date().toISOString()
        }));

        dispatch(addMessage({
            role: "assistant",
            content: "",
            created_at: new Date().toISOString()
        }));

        let sessionId = payload.session_id ?? null;
        let hasError = false;

        await AssistentService.chatWithAssistent(payload, {
            onChunk: (chunk) => {
                const text = chunk?.content ?? "";

                if (text) {
                    dispatch(appendToLastMessage(text));
                }

                if (chunk?.session_id) {
                    sessionId = chunk.session_id;
                }
            },
            onDone: () => {
                if (hasError) return;

                if (!payload.session_id && sessionId) {
                    dispatch(generateSessionTitle(sessionId, payload.user_id, payload.company_id, payload.target_language));
                }

                if (sessionId) {
                    dispatch(loadAiUserSession(sessionId, payload.user_id, payload.company_id, payload.limit));
                }

                dispatch(loadAiSessions(payload.user_id, payload.company_id, payload.limit));
                dispatch(setLoading(false));
            },
            onError: (error) => {
                hasError = true;

                const message =
                    error?.ApiError?.message ||
                    error?.message ||
                    error?.detail ||
                    JSON.stringify(error) ||
                    "AI error";

                //console.error("AI stream error:", error);

                dispatch(setError(message));
                dispatch(setLoading(false));
            },
        });

    } catch (e) {
        handleError(e, dispatch);
    }
};*/

export const sendMessageToAi = (payload) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        dispatch(setError(null));
        dispatch(setRequestTimestamp());
        dispatch(addMessage({
            role: "user",
            content: payload.message,
            created_at: new Date().toISOString()
        }));

        const response = await AssistentService.chatWithAssistent(payload);

        dispatch(addMessage({
            role: "assistant",
            content: response?.content ?? "",
            created_at: new Date().toISOString()
        }));

        const sessionId = response?.session_id ?? payload.session_id ?? null;

        if (!payload.session_id && sessionId) {
            dispatch(generateSessionTitle(sessionId, payload.user_id, payload.company_id, payload.target_language));
        }

        if (sessionId) {
            dispatch(loadAiUserSession(sessionId, payload.user_id, payload.company_id, payload.limit));
        }
        dispatch(loadAiSessions(payload.user_id, payload.company_id, payload.limit));

    } catch (e) {
        handleError(e, dispatch);
    } finally {
        dispatch(setLoading(false));
    }
};

export const checkAndNotifySlowError = () => (dispatch, getState) => {
    const { requestTimestamp, errorNotified } = getState().aiAssistant;

    if (errorNotified || !requestTimestamp) return;

    const elapsed = Date.now() - new Date(requestTimestamp).getTime();

    if (elapsed >= 60_000) {
        dispatch(setErrorNotified(true));
    }
};

export const generateSessionTitle = (sessionId, userId, companyId, targetLanguage) => async (dispatch) => {
    try {
        dispatch(setTitleLoadingError(null));
        dispatch(setTitleLoading(sessionId));
        const response = await AssistentService.generateSessionTitle(sessionId, userId, companyId, targetLanguage);
        dispatch(loadAiSessions(userId, companyId))
    } catch (e) {
        handleError(e, dispatch, 'title');
    } finally {
        dispatch(setTitleLoading(null));
    }
};

export const loadAiSessions = (userId, companyId, limit = 200) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        dispatch(setError(null));
        const sessions = await AssistentService.getUserSessionsList(
            userId,
            companyId,
            limit
        );
        dispatch(setSessions(sessions));

    } catch (e) {
        handleError(e, dispatch);
    } finally {
        dispatch(setLoading(false));
    }
};

export const loadAiUserSession = (sessionId, userId, companyId, limit = 200) => async (dispatch) => {

    try {
        dispatch(setLoading(true));
        dispatch(setError(null));
        const session = await AssistentService.getUserSession(
            sessionId,
            userId,
            companyId,
            limit
        );

        dispatch(setSession(session));
        return session;

    } catch (e) {
        handleError(e, dispatch);
    } finally {
        dispatch(setLoading(false));
    }
};

export const loadAiStatistic = (
    from_date,
    to_date,
    user_id,
    company_id,
    limit = 100
    ) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        dispatch(setError(null));
        const statistic = await AssistentService.getUsageStatistics(
            from_date,
            to_date,
            limit,
            user_id,
            company_id
        );
        dispatch(setStatistic(statistic));
        return statistic;
    } catch (e) {
        handleError(e, dispatch);
    } finally {
        dispatch(setLoading(false));
    }
};

export const loadAiHealth = () => async (dispatch) => {
    try {
        const health = await AssistentService.getHealth();
        dispatch(setHealth(health));
        dispatch(setError(null));
        return health;
    } catch (e) {
        handleError(e, dispatch);
    }
};


export const loadAiModels = () => async (dispatch) => {
    try {
        const data = await AssistentService.getModels();
        dispatch(setModels(data));
        dispatch(setError(null));
        return data;
    } catch (e) {
        handleError(e, dispatch);
    }
};

export const clearAiChat = () => (dispatch) => {
    dispatch(clearChat());
};

export default aiAssistantSlice.reducer;